/*
 * Photo flow — Prepare, Capture, Verify, Deliver.
 *
 * The captured image is held in memory as a Blob and never survives a reload,
 * which is why the flow is one page and nothing here navigates. The bytes reach
 * S3 by presigned PUT and reach Shopify not at all: the cart carries only
 * `_photoId`, and every API call goes through `apiUrl`, which carries the
 * market the proxy cannot work out for itself.
 */
(() => {
  const root = document.querySelector('[data-photo-flow]');
  if (!root) return;

  const config = {
    mode: root.dataset.mode === 'upload' ? 'upload' : 'camera',
    minWidth: Number(root.dataset.minWidthPx) || 0,
    minHeight: Number(root.dataset.minHeightPx) || 0,
    maxBytes: (Number(root.dataset.maxFileSizeMb) || 10) * 1024 * 1024,
    productId: root.dataset.productId || '',
    // Carries the market prefix already; never rebuild it from a literal.
    proxyBase: root.dataset.proxyBase || '/apps/photo',
    market: root.dataset.market || '',
    widthMm: Number(root.dataset.widthMm) || 0,
    heightMm: Number(root.dataset.heightMm) || 0,
  };

  function apiUrl(path) {
    const url = `${config.proxyBase}${path}`;

    return config.market ? `${url}?market=${encodeURIComponent(config.market)}` : url;
  }

  const el = {
    tip: root.querySelector('[data-photo-flow-tip]'),
    steps: root.querySelector('[data-photo-flow-steps]'),
    progress: root.querySelector('[data-photo-flow-progress]'),
    panels: [...root.querySelectorAll('[data-photo-flow-panel]')],
    viewport: root.querySelector('[data-photo-flow-viewport]'),
    video: root.querySelector('[data-photo-flow-video]'),
    canvas: root.querySelector('[data-photo-flow-canvas]'),
    shutter: root.querySelector('[data-photo-flow-shutter]'),
    shot: root.querySelector('[data-photo-flow-shot]'),
    retakeCaptures: [...root.querySelectorAll('[data-photo-flow-retake-capture]')],
    cameraError: root.querySelector('[data-photo-flow-camera-error]'),
    dropzone: root.querySelector('[data-photo-flow-dropzone]'),
    uploadEmpty: root.querySelector('[data-photo-flow-upload-empty]'),
    uploadReady: root.querySelector('[data-photo-flow-upload-ready]'),
    uploadError: root.querySelector('[data-photo-flow-upload-error]'),
    readyThumb: root.querySelector('[data-photo-flow-ready-thumb]'),
    readyName: root.querySelector('[data-photo-flow-ready-name]'),
    readyFormat: root.querySelector('[data-photo-flow-ready-format]'),
    readyResolution: root.querySelector('[data-photo-flow-ready-resolution]'),
    readyFilesize: root.querySelector('[data-photo-flow-ready-filesize]'),
    readyAgain: root.querySelector('[data-photo-flow-ready-again]'),
    fallback: root.querySelector('[data-photo-flow-camera-fallback]'),
    verify: root.querySelector('[data-photo-flow-verify]'),
    preview: root.querySelector('[data-photo-flow-preview]'),
    scanbar: root.querySelector('[data-photo-flow-scanbar]'),
    scanProgress: root.querySelector('[data-photo-flow-scan-progress]'),
    scanCaption: root.querySelector('[data-photo-flow-scan-caption]'),
    scanElapsed: root.querySelector('[data-photo-flow-scan-elapsed]'),
    scanBar: root.querySelector('[data-photo-flow-scan-bar]'),
    checks: root.querySelector('[data-photo-flow-checks]'),
    attempts: root.querySelector('[data-photo-flow-attempts]'),
    retake: root.querySelector('[data-photo-flow-retake]'),
    retry: root.querySelector('[data-photo-flow-retry]'),
    deliver: root.querySelector('[data-photo-flow-deliver]'),
    deliveryCards: [...root.querySelectorAll('[data-photo-flow-delivery]')],
    deliveryEmpty: root.querySelector('[data-photo-flow-delivery-empty]'),
    addToCart: root.querySelector('[data-photo-flow-add-to-cart]'),
    ctaLabel: root.querySelector('[data-photo-flow-cta-label]'),
    cartError: root.querySelector('[data-photo-flow-cart-error]'),
    checkoutForm: root.querySelector('[data-photo-flow-checkout-form]'),
    deliverChecks: root.querySelector('[data-photo-flow-deliver-checks]'),
    booths: root.querySelector('[data-photo-flow-booths]'),
    boothList: root.querySelector('[data-photo-flow-booth-list]'),
    boothTemplate: root.querySelector('[data-photo-flow-booth-template]'),
    boothDistanceIcon: root.querySelector('[data-photo-flow-booth-distance-icon]'),
    boothLocate: root.querySelector('[data-photo-flow-booth-locate]'),
    flip: root.querySelector('[data-photo-flow-flip]'),
    resumed: root.querySelector('[data-photo-flow-resumed]'),
  };

  const SLOW_AFTER_MS = 35000;
  const VERIFY_TIMEOUT_MS = 90000;
  const SESSION_KEY = 'sp-photo-flow-active';
  const MAX_CANVAS_EDGE = 4000;

  function message(name, values = {}) {
    let text = root.dataset[name] || '';
    for (const [token, value] of Object.entries(values)) text = text.replace(token, String(value));
    return text;
  }

  function rememberSession(active) {
    if (active && el.resumed) el.resumed.dataset.visible = 'false';
    try {
      if (active) sessionStorage.setItem(SESSION_KEY, config.productId);
      else sessionStorage.removeItem(SESSION_KEY);
    } catch {}
  }

  try {
    if (sessionStorage.getItem(SESSION_KEY) === config.productId && el.resumed) {
      el.resumed.dataset.visible = 'true';
    }
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}

  /** The captured or chosen image. Cleared only by an explicit retake. */
  let photo = null;
  let stream = null;
  let step = 1;

  /** Server-side record id, returned by /upload-url and carried into the cart. */
  let photoId = null;
  let previewUrl = null;
  let readyUrl = null;
  let shotUrl = null;
  let verifying = false;
  let scanAnimation = null;
  let scanWalk = null;
  let scanClock = null;
  let scanBegan = 0;
  let progressDone = 0;
  let marketLoaded = false;
  let facing = 'user';
  let cameraPending = false;
  let cameraRestart = false;
  let cameraRequest = 0;
  let slowTimer = null;

  // --- step machine ---------------------------------------------------------

  function render() {
    el.panels.forEach((panel) => {
      panel.dataset.active = String(Number(panel.dataset.photoFlowPanel) === step);
    });

    if (el.steps) {
      el.steps.dataset.current = String(step);
      el.steps.querySelectorAll('[data-step]').forEach((node) => {
        const index = Number(node.dataset.step);
        node.dataset.state = index < step ? 'done' : index === step ? 'current' : 'upcoming';
        if (index === step) node.setAttribute('aria-current', 'step');
        else node.removeAttribute('aria-current');
      });
    }

    if (el.progress) el.progress.style.width = `${step * 25}%`;
    // The lighting tip belongs to the capture step, camera or upload.
    if (el.tip) el.tip.dataset.visible = String(step === 2);

    root.dataset.step = String(step);
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const heading = root
      .querySelector(`[data-photo-flow-panel="${step}"]`)
      ?.querySelector('h1, h2, h3');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }

  function scrollToFlowTop() {
    const jump = () => {
      const top = root.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'instant' });
    };
    requestAnimationFrame(jump);
    setTimeout(jump, 250);
    setTimeout(jump, 700);
  }

  function advance(to) {
    const target = Math.min(Math.max(to, 1), 4);

    // Steps 3 and 4 mean nothing without an image. Landing on either without one
    // is a back-button or a stale link, so fall back to capture rather than
    // rendering an empty frame.
    if (target > 2 && !photo) {
      step = 2;
      render();
      if (config.mode === 'camera') startCamera();
      return;
    }

    step = target;
    render();

    if (step === 2 && config.mode === 'camera') resumeCapture();
    else stopCamera();

    if (step === 3 && !verifying && !photoId) startVerification();
    if (step === 4) {
      if (el.deliverChecks && el.scanProgress) el.deliverChecks.textContent = el.scanProgress.textContent;
      loadMarketConfig();
    }
  }

  root.addEventListener('click', (event) => {
    const target = event.target.closest('[data-photo-flow-advance]');
    if (target) advance(Number(target.dataset.photoFlowAdvance));
  });

  // --- camera ---------------------------------------------------------------

  function setCaptureState(state) {
    const taken = state === 'taken';
    root.dataset.taken = String(taken);
    if (taken && el.flip) el.flip.dataset.visible = 'false';
    if (taken && el.fallback) el.fallback.dataset.visible = 'false';
  }

  function showShot() {
    if (!photo || !el.shot) return;
    if (shotUrl) URL.revokeObjectURL(shotUrl);
    shotUrl = URL.createObjectURL(photo.blob);
    el.shot.src = shotUrl;
  }

  function clearShot() {
    if (!shotUrl) return;
    URL.revokeObjectURL(shotUrl);
    shotUrl = null;
    if (el.shot) el.shot.removeAttribute('src');
  }

  function resumeCapture() {
    if (photo) {
      setCaptureState('taken');
      return;
    }
    setCaptureState('live');
    startCamera();
  }

  function showFallback(kind) {
    if (!el.fallback) return;
    el.fallback.dataset.visible = 'true';
    // With no video to frame, the viewport drops its 4:5 / 4:3 ratio and squares up.
    el.viewport?.setAttribute('data-camera-fallback', 'true');
    // Copy comes from Liquid so it stays translatable.
    const title = el.fallback.querySelector('[data-photo-flow-fallback-title]');
    const body = el.fallback.querySelector('[data-photo-flow-fallback-body]');
    const cta = el.fallback.querySelector('[data-photo-flow-fallback-cta]');
    const file = el.fallback.querySelector('[data-photo-flow-file]');
    if (title) title.textContent = el.fallback.dataset[`${kind}Title`] || '';
    const ios = kind === 'denied' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (body) body.textContent = (ios && el.fallback.dataset.deniedIosBody) || el.fallback.dataset[`${kind}Body`] || '';
    if (cta) cta.textContent = el.fallback.dataset[`${kind}Cta`] || '';
    // 'missing' means no camera hardware, so capture= would open a picker that
    // cannot deliver — leave it off and let the button read as an upload.
    if (file) {
      if (kind === 'missing') file.removeAttribute('capture');
      else file.setAttribute('capture', 'user');
    }
    if (el.shutter) el.shutter.disabled = true;
  }

  async function startCamera() {
    if (cameraPending) {
      cameraRestart = true;
      return;
    }
    if (stream || !el.video) return;

    // Instagram and Facebook in-app browsers restrict getUserMedia, and ad
    // traffic lands there — the file-input fallback is a main path.
    if (!navigator.mediaDevices?.getUserMedia) {
      showFallback('unsupported');
      return;
    }

    cameraPending = true;
    const request = ++cameraRequest;

    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 1600 } },
        audio: false,
      });
      if (request !== cameraRequest || step !== 2 || root.dataset.taken === 'true') {
        next.getTracks().forEach((track) => track.stop());
        return;
      }
      stream = next;
      el.video.srcObject = stream;
      // A retry can succeed after an earlier refusal — clear the fallback, or it
      // stays over a working camera and the viewport stays square.
      if (el.fallback) el.fallback.dataset.visible = 'false';
      el.viewport?.removeAttribute('data-camera-fallback');
      if (el.shutter) el.shutter.disabled = false;
      showFlipWhenUseful();
    } catch (error) {
      if (request === cameraRequest) showFallback(error?.name === 'NotFoundError' ? 'missing' : 'denied');
    } finally {
      cameraPending = false;
      if (cameraRestart) {
        cameraRestart = false;
        if (!stream && step === 2 && root.dataset.taken !== 'true') startCamera();
      }
    }
  }

  async function showFlipWhenUseful() {
    if (!el.flip || !navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput').length;
      el.flip.dataset.visible = String(cameras > 1 && root.dataset.taken !== 'true');
    } catch {}
  }

  el.flip?.addEventListener('click', () => {
    facing = facing === 'user' ? 'environment' : 'user';
    stopCamera();
    startCamera();
  });

  function stopCamera() {
    cameraRequest += 1;
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
    if (el.video) el.video.srcObject = null;
  }

  el.shutter?.addEventListener('click', () => {
    if (!stream || !el.video || !el.canvas) return;

    const { videoWidth: width, videoHeight: height } = el.video;
    if (!width || !height) return;

    if (width < config.minWidth || height < config.minHeight) {
      fail(message('errorTooSmall', { __W__: config.minWidth, __H__: config.minHeight }));
      return;
    }

    el.canvas.width = width;
    el.canvas.height = height;
    el.canvas.getContext('2d').drawImage(el.video, 0, 0, width, height);

    el.canvas.toBlob(
      (blob) => {
        if (!blob) return;
        photo = { blob, width, height, source: 'camera' };
        rememberSession(true);
        stopCamera();
        showShot();
        setCaptureState('taken');
      },
      'image/jpeg',
      0.92,
    );
  });

  // --- upload ---------------------------------------------------------------

  /**
   * The camera error pill lives inside the viewport, so it does not exist in
   * upload mode. Without the second branch every rejected file failed silently.
   */
  function fail(message) {
    if (el.cameraError) {
      el.cameraError.textContent = message;
      el.cameraError.dataset.visible = 'true';
    }
    if (el.uploadError) {
      el.uploadError.textContent = message;
      el.uploadError.dataset.visible = 'true';
    }
    root.dispatchEvent(new CustomEvent('photo-flow:error', { detail: { message } }));
  }

  function clearFileError() {
    if (el.cameraError) el.cameraError.dataset.visible = 'false';
    if (el.uploadError) el.uploadError.dataset.visible = 'false';
  }

  function showUploadState(state) {
    if (el.uploadEmpty) el.uploadEmpty.dataset.visible = String(state === 'empty');
    if (el.uploadReady) el.uploadReady.dataset.visible = String(state === 'ready');
  }

  function formatBytes(bytes) {
    const mb = bytes / 1024 / 1024;
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  }

  /** The chosen file's own type, not the JPEG we re-encode it to. */
  function formatName(file) {
    const subtype = (file.type || '').split('/')[1] || '';
    if (subtype === 'jpeg' || subtype === 'jpg') return 'JPEG';
    if (subtype === 'heic' || subtype === 'heif') return 'HEIC';
    return subtype ? subtype.toUpperCase() : '—';
  }

  function renderReady(file, image) {
    if (readyUrl) URL.revokeObjectURL(readyUrl);
    readyUrl = URL.createObjectURL(photo.blob);

    if (el.readyThumb) el.readyThumb.src = readyUrl;
    if (el.readyName) el.readyName.textContent = `${file.name} · ${formatBytes(file.size)}`;
    if (el.readyFormat) el.readyFormat.textContent = formatName(file);
    if (el.readyResolution) {
      el.readyResolution.textContent = `${image.naturalWidth} × ${image.naturalHeight} px`;
    }
    if (el.readyFilesize) el.readyFilesize.textContent = formatBytes(file.size);
  }

  function resetUpload() {
    photo = null;
    photoId = null;
    if (readyUrl) {
      URL.revokeObjectURL(readyUrl);
      readyUrl = null;
    }
    // Without this, re-picking the same file fires no `change` event.
    root.querySelectorAll('[data-photo-flow-file]').forEach((input) => {
      input.value = '';
    });
    clearFileError();
    showUploadState('empty');
  }

  el.readyAgain?.addEventListener('click', resetUpload);

  /**
   * HEIC comes off iOS, and iOS Safari can decode it into a canvas, which is
   * where it matters. Browsers that cannot decode it fail the image load and get
   * a clear message rather than a silent empty canvas.
   *
   * TODO(photo-flow): a libheif build would cover desktop Chrome and Firefox,
   * at roughly 1.5 MB. Not worth it until the upload path has real traffic.
   */
  function readImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode-failed'));
      };
      image.src = url;
    });
  }

  async function acceptFile(file) {
    if (!file) return;
    clearFileError();

    if (file.size > config.maxBytes) {
      fail(message('errorTooLarge', { __MB__: Math.round(config.maxBytes / 1024 / 1024) }));
      return;
    }

    let image;
    try {
      image = await readImage(file);
    } catch {
      fail(message('errorUnreadable'));
      return;
    }

    if (image.naturalWidth < config.minWidth || image.naturalHeight < config.minHeight) {
      fail(message('errorTooSmall', { __W__: config.minWidth, __H__: config.minHeight }));
      return;
    }

    // Re-encode everything to JPEG so HEIC never reaches the API, and so the
    // upload size is predictable regardless of what the device produced.
    const scale = Math.min(1, MAX_CANVAS_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.round(image.naturalWidth * scale);
    const height = Math.round(image.naturalHeight * scale);

    try {
      el.canvas.width = width;
      el.canvas.height = height;
      el.canvas.getContext('2d').drawImage(image, 0, 0, width, height);
    } catch {
      fail(message('errorPrepare'));
      return;
    }

    el.canvas.toBlob(
      (blob) => {
        if (!blob) {
          fail(message('errorPrepare'));
          return;
        }
        photo = {
          blob,
          width,
          height,
          source: 'upload',
        };
        rememberSession(true);

        // Both modes confirm before verifying; the camera fallback shares this
        // path and lands on the same taken state a shutter press would.
        if (config.mode === 'upload' && el.uploadReady) {
          renderReady(file, image);
          showUploadState('ready');
          scrollToFlowTop();
        } else {
          showShot();
          setCaptureState('taken');
        }
      },
      'image/jpeg',
      0.92,
    );
  }

  root.querySelectorAll('[data-photo-flow-browse]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      button.closest('[data-photo-flow-dropzone], [data-photo-flow-camera-fallback]')
        ?.querySelector('[data-photo-flow-file]')
        ?.click();
    });
  });

  root.querySelectorAll('[data-photo-flow-file]').forEach((input) => {
    input.addEventListener('change', () => acceptFile(input.files?.[0]));
  });

  if (el.dropzone) {
    ['dragenter', 'dragover'].forEach((type) =>
      el.dropzone.addEventListener(type, (event) => {
        event.preventDefault();
        el.dropzone.dataset.dragging = 'true';
      }),
    );
    ['dragleave', 'drop'].forEach((type) =>
      el.dropzone.addEventListener(type, (event) => {
        event.preventDefault();
        el.dropzone.dataset.dragging = 'false';
      }),
    );
    el.dropzone.addEventListener('drop', (event) => acceptFile(event.dataTransfer?.files?.[0]));
    el.dropzone.addEventListener('click', (event) => {
      if (event.target.closest('[data-photo-flow-file]')) return;
      el.dropzone.querySelector('[data-photo-flow-file]')?.click();
    });
  }

  // --- verify ---------------------------------------------------------------

  /**
   * The scan is choreography over a single request: idphoto.ai returns every
   * check at once (CLAUDE.md §7). The bar sweeps until the response lands and a
   * minimum time has passed, then the checks resolve one at a time with their
   * real verdicts — progressive to watch, but never showing a result before the
   * API has given one.
   */
  const MIN_SCAN_MS = 2400;
  const RESOLVE_STEP_MS = 260;
  const ACTIVE_STEP_MS = 900;

  /**
   * The five cards the customer sees, and the checks the app derives behind each.
   * Grouping is presentation only — /verify still returns the eight issue-derived
   * checks. A group passes only when every check inside it passes.
   *
   * `background` is deliberately empty. idphoto.ai replaces the background rather
   * than grading it, so there is no code to map and the card reports "Replaced".
   */
  const CHECK_GROUPS = {
    face_position: ['face_detected', 'head_facing_forward', 'head_not_tilted'],
    lighting: ['good_brightness', 'balanced_lighting'],
    background: [],
    expression: ['neutral_expression', 'no_eyewear', 'no_headwear'],
    requirements: ['quality_photo', 'framing', 'in_colour'],
  };

  const checkText = (() => {
    try {
      return JSON.parse(el.checks?.dataset.checkLabels || '{}');
    } catch {
      return {};
    }
  })();

  function checkLabel(check) {
    return checkText[check.code] || check.label || check.code;
  }

  function checkMessage(check) {
    if (check.code === 'no_eyewear' && check.state === 'warning') {
      return el.checks?.dataset.glassesWarning || null;
    }
    return null;
  }

  function holdFor(ms) {
    return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
  }

  function setStage(stage) {
    if (el.verify) el.verify.dataset.stage = stage;
  }

  function checkRows() {
    return [...(el.checks?.querySelectorAll('[data-check]') ?? [])];
  }

  function setProgress(done) {
    const total = checkRows().length;
    progressDone = Math.min(Math.max(progressDone, done), total);

    if (el.scanProgress) {
      el.scanProgress.textContent = (el.scanProgress.dataset.template || '')
        .replace('__DONE__', String(progressDone))
        .replace('__TOTAL__', String(total));
    }

    if (el.scanBar) el.scanBar.style.width = `${total ? (progressDone / total) * 100 : 0}%`;
  }

  function setAttempts(remaining) {
    if (!el.attempts || typeof remaining !== 'number') return;
    el.attempts.textContent =
      remaining > 0
        ? (el.attempts.dataset.template || '').replace('__COUNT__', String(remaining))
        : el.attempts.dataset.none || '';
    el.attempts.dataset.visible = 'true';
    // The cap is per photo record, and a retake creates a new one — so retaking
    // stays open when re-checking these same bytes does not.
    if (el.retry) el.retry.disabled = remaining <= 0;
  }

  function setCaption(row) {
    if (!el.scanCaption || !row) return;
    const caption = row.dataset.checkCaption;
    if (caption) el.scanCaption.textContent = caption;
  }

  // Walks the highlight down the cards while the request is in flight. No card
  // gets a verdict here — the response has not landed, and a tick before the API
  // answers is a guess.
  function startScan() {
    // translateY percentages resolve against the bar's own height, so the end of
    // the sweep has to be measured off the frame rather than written as one.
    const travel = el.scanbar?.parentElement?.clientHeight || 0;
    if (el.scanbar?.animate && travel) {
      scanAnimation = el.scanbar.animate(
        [{ transform: 'translateY(-100%)' }, { transform: `translateY(${travel}px)` }],
        { duration: 1500, iterations: Infinity, easing: 'ease-in-out' },
      );
    }

    const rows = checkRows();
    if (!rows.length) return;

    let index = 0;
    rows[0].dataset.state = 'active';
    setCaption(rows[0]);
    setProgress(0);

    scanWalk = setInterval(() => {
      rows[index].dataset.state = 'pending';
      index = (index + 1) % rows.length;
      rows[index].dataset.state = 'active';
      setCaption(rows[index]);
    }, ACTIVE_STEP_MS);

    scanClock = setInterval(() => {
      if (!el.scanElapsed) return;
      el.scanElapsed.textContent = (el.scanElapsed.dataset.template || '').replace(
        '__S__',
        String(Math.round((Date.now() - scanBegan) / 1000)),
      );
    }, 1000);
  }

  // Leaves the sweep running and the last card spinning: applyResult is about to
  // give every card a verdict, so dimming it here would only flicker.
  function stopWalk() {
    clearInterval(scanWalk);
    clearInterval(scanClock);
    scanWalk = null;
    scanClock = null;
  }

  function stopScan() {
    scanAnimation?.cancel();
    scanAnimation = null;
    stopWalk();
    checkRows().forEach((row) => {
      if (row.dataset.state === 'active') row.dataset.state = 'pending';
    });
  }

  function resetChecks() {
    root.querySelectorAll('[data-check-extra]').forEach((row) => row.remove());
    checkRows().forEach((row) => {
      row.dataset.state = 'pending';
      const message = row.querySelector('[data-check-message]');
      if (message) {
        message.textContent = '';
        message.dataset.filled = 'false';
      }
    });
    if (el.scanElapsed) el.scanElapsed.textContent = '';
    progressDone = 0;
    setProgress(0);
  }

  function showLocalPreview() {
    if (!photo) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(photo.blob);
    if (el.preview) el.preview.src = previewUrl;
  }

  async function apiPost(path, body, signal) {
    const response = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.ok) {
      const error = new Error(data?.error || `http_${response.status}`);
      error.payload = data;
      throw error;
    }
    return data;
  }

  function putWithProgress(url, contentType, blob, signal) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', url);
      request.setRequestHeader('Content-Type', contentType);
      request.upload.onprogress = (event) => {
        if (!event.lengthComputable || !el.scanCaption || !el.verify) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        el.scanCaption.textContent = (el.verify.dataset.uploadTemplate || '').replace('__P__', String(percent));
      };
      request.onload = () =>
        request.status >= 200 && request.status < 300 ? resolve() : reject(new Error('upload_failed'));
      request.onerror = () => reject(new Error('upload_failed'));
      request.onabort = () => reject(new DOMException('aborted', 'AbortError'));
      signal?.addEventListener('abort', () => request.abort());
      request.send(blob);
    });
  }

  function errorKind(error) {
    if (error?.name === 'AbortError') return 'timeout';
    if (error?.message === 'verification_limit_reached') return 'limit';
    return 'vendor';
  }

  async function runVerification(signal) {
    // A retry re-checks the image already in the bucket. Re-uploading would
    // spend a second verification on identical bytes, and they are capped.
    if (!photoId) {
      const upload = await apiPost(
        '/upload-url',
        {
          productId: config.productId,
          contentType: photo.blob.type || 'image/jpeg',
          contentLength: photo.blob.size,
        },
        signal,
      );

      await putWithProgress(upload.uploadUrl, upload.contentType, photo.blob, signal);

      photoId = upload.photoId;
      if (el.scanCaption) setCaption(checkRows()[0]);
    }

    return apiPost('/verify', { photoId }, signal);
  }

  function appendCheckRow(check) {
    const template = checkRows()[0];
    if (!template || !el.checks) return;

    const row = template.cloneNode(true);
    row.dataset.check = check.code;
    row.dataset.state = check.state || 'fail';
    row.setAttribute('data-check-extra', 'true');

    const label = row.querySelector('[data-check-label]');
    if (label) label.textContent = checkLabel(check);

    // The clone carries the first card's description, which belongs to a
    // different check entirely.
    const note = row.querySelector('[data-check-note]');
    if (note) note.textContent = '';

    const message = row.querySelector('[data-check-message]');
    if (message) {
      message.textContent = '';
      message.dataset.filled = 'false';
    }

    el.checks.append(row);
  }

  /** A group is only as good as its worst member. */
  function groupState(states) {
    if (states.includes('fail')) return 'fail';
    if (states.includes('warning')) return 'warning';
    return 'pass';
  }

  async function applyResult(data) {
    stopWalk();

    const byCode = new Map((data.checks ?? []).map((check) => [check.code, check]));
    const grouped = new Set(Object.values(CHECK_GROUPS).flat());
    const rows = checkRows();
    rows.forEach((row) => {
      row.dataset.state = 'pending';
    });

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      row.dataset.state = 'active';
      setCaption(row);
      // eslint-disable-next-line no-await-in-loop
      await holdFor(RESOLVE_STEP_MS);

      const members = (CHECK_GROUPS[row.dataset.check] ?? []).map((code) => byCode.get(code));
      const known = members.filter(Boolean);

      row.dataset.state = groupState(known.map((check) => check.state));

      // Name the checks that actually went wrong, so "Lighting Quality" is not
      // the only thing the customer has to work from.
      const problems = known.filter((check) => check.state !== 'pass');
      const explained = problems.map(checkMessage).find(Boolean);
      const message = row.querySelector('[data-check-message]');
      if (message && problems.length) {
        message.textContent = explained ?? problems.map(checkLabel).join(' · ');
        message.dataset.filled = 'true';
      }

      setProgress(index + 1);
    }

    // The app returns a check for any ISSUE_* code it could not map. Nothing
    // groups it, so it gets its own card rather than reading as a silent pass.
    (data.checks ?? []).filter((check) => !grouped.has(check.code)).forEach(appendCheckRow);

    setAttempts(data.verificationsRemaining);
    setStage(data.status === 'passed' ? 'passed' : 'failed');
    stopScan();
  }

  async function startVerification() {
    if (!photo || verifying) return;

    verifying = true;
    if (el.verify) {
      el.verify.dataset.slow = 'false';
      el.verify.dataset.error = 'vendor';
    }
    setStage('scanning');
    resetChecks();
    showLocalPreview();
    scanBegan = Date.now();
    startScan();

    if (el.attempts) el.attempts.dataset.visible = 'false';

    const began = scanBegan;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);
    clearTimeout(slowTimer);
    slowTimer = setTimeout(() => {
      if (el.verify) el.verify.dataset.slow = 'true';
    }, SLOW_AFTER_MS);

    try {
      const result = await runVerification(controller.signal);
      await holdFor(MIN_SCAN_MS - (Date.now() - began));
      await applyResult(result);
    } catch (error) {
      // A vendor error is not a rejection. Telling a customer their photo failed
      // when the API fell over loses a sale that was already made.
      await holdFor(600);
      if (el.verify) el.verify.dataset.error = errorKind(error);
      setStage('error');
      stopScan();
      root.dispatchEvent(new CustomEvent('photo-flow:verify-error', { detail: { error } }));
    } finally {
      clearTimeout(timeout);
      clearTimeout(slowTimer);
      if (el.verify) el.verify.dataset.slow = 'false';
      verifying = false;
    }
  }

  el.retry?.addEventListener('click', () => startVerification());

  function retakePhoto() {
    // A new image is a new record: the old photoId keeps its own verification
    // count and its own object in the bucket.
    resetUpload();
    clearShot();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrl = null;
    }
    resetChecks();
    if (el.retry) el.retry.disabled = false;
    setStage('scanning');
    advance(2);
  }

  el.retake?.addEventListener('click', retakePhoto);
  el.retakeCaptures.forEach((button) => button.addEventListener('click', retakePhoto));

  // --- deliver --------------------------------------------------------------

  const CAPITALS = {
    SE: [59.3293, 18.0686],
    DK: [55.6761, 12.5683],
    FI: [60.1699, 24.9384],
    NO: [59.9139, 10.7522],
    GB: [51.5072, -0.1276],
  };

  let booths = null;
  let boothsLoading = null;

  function selectedCard() {
    return el.deliveryCards.find(
      (card) =>
        card.dataset.available === 'true' &&
        card.querySelector('[data-photo-flow-delivery-input]')?.checked,
    );
  }

  function updateSelection() {
    const method = selectedCard()?.dataset.method || '';

    if (el.deliver) el.deliver.dataset.selected = method;

    el.checkoutForm?.querySelectorAll('[data-required-for]').forEach((field) => {
      field.required = field.dataset.requiredFor === method;
    });

    if (method === 'booth') loadBooths();
  }

  el.deliveryCards.forEach((card) => {
    card.querySelector('[data-photo-flow-delivery-input]')?.addEventListener('change', updateSelection);
  });

  function applyDeliveryAvailability(methods) {
    const available = el.deliveryCards.filter((card) => {
      const inStock = !card.querySelector('[data-photo-flow-delivery-input]')?.disabled;
      const ok = inStock && (!methods || methods.includes(card.dataset.method));
      card.dataset.available = String(ok);
      return ok;
    });

    if (el.deliveryEmpty) el.deliveryEmpty.dataset.visible = String(available.length === 0);
    if (el.addToCart) el.addToCart.disabled = available.length === 0;

    const alreadyChosen = available.some(
      (card) => card.querySelector('[data-photo-flow-delivery-input]')?.checked,
    );

    if (!alreadyChosen) {
      // Booth is preselected per the frame, but only where it survived the filter.
      const preferred = available.find((card) => card.dataset.method === 'booth') ?? available[0];
      const input = preferred?.querySelector('[data-photo-flow-delivery-input]');
      if (input) input.checked = true;
    }

    updateSelection();
  }

  async function loadMarketConfig() {
    if (marketLoaded) return;
    marketLoaded = true;

    let methods = null;

    try {
      const response = await fetch(apiUrl('/market-config'), {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (data?.ok && Array.isArray(data.deliveryMethods)) methods = data.deliveryMethods;
    } catch {
      // Leave `methods` null so every card renders. A customer who cannot pick a
      // delivery method cannot buy at all, which is worse than offering one that
      // a later check refunds.
    }

    applyDeliveryAvailability(methods);
  }

  function distanceKm([lat1, lng1], [lat2, lng2]) {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLng = (lng2 - lng1) * rad;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;

    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatKm(km) {
    const digits = km < 10 ? 1 : 0;
    const number = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(km);

    return (el.booths?.dataset.distanceTemplate || '__KM__ km').replace('__KM__', number);
  }

  function renderBooths(origin, showDistance) {
    if (!el.boothList || !el.boothTemplate || !booths?.length) return;

    const nearest = booths
      .map((booth) => ({ booth, km: distanceKm(origin, [booth.lat, booth.lng]) }))
      .sort((a, b) => a.km - b.km)
      .slice(0, 3);

    el.boothList.replaceChildren(
      ...nearest.map(({ booth, km }) => {
        const item = el.boothTemplate.content.firstElementChild.cloneNode(true);
        item.querySelector('[data-booth-name]').textContent = booth.name;
        item.querySelector('[data-booth-city]').textContent = booth.address ?? booth.city;

        if (showDistance) {
          const distance = item.querySelector('[data-booth-distance]');
          const icon = el.boothDistanceIcon?.content.firstElementChild?.cloneNode(true);
          if (icon) distance.append(icon);
          distance.append(formatKm(km));
        }

        return item;
      }),
    );
  }

  function locate(prompted) {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        renderBooths([position.coords.latitude, position.coords.longitude], true);
        if (el.boothLocate) el.boothLocate.dataset.visible = 'false';
      },
      () => {
        if (prompted && el.boothLocate) el.boothLocate.dataset.visible = 'false';
      },
      { maximumAge: 600000, timeout: 10000 },
    );
  }

  function loadBooths() {
    if (boothsLoading || !el.booths || !el.deliver) return;

    const country = el.deliver.dataset.country;

    boothsLoading = fetch(el.deliver.dataset.boothsUrl)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      })
      .catch(() => {
        boothsLoading = null;
        return null;
      })
      .then(async (all) => {
        if (all === null) return;
        booths = Array.isArray(all) ? all : [];
        el.booths.dataset.hasBooths = String(booths.length > 0);
        if (!booths.length) return;

        const capital = CAPITALS[country] ?? [booths[0].lat, booths[0].lng];
        renderBooths(capital, false);

        let state = 'prompt';
        try {
          state = (await navigator.permissions?.query({ name: 'geolocation' }))?.state ?? 'prompt';
        } catch {
          state = 'prompt';
        }

        if (state === 'granted') locate(false);
        else if (state === 'prompt' && navigator.geolocation && el.boothLocate) {
          el.boothLocate.dataset.visible = 'true';
        }
      });
  }

  el.boothLocate?.addEventListener('click', () => locate(true));

  function base64Url(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fieldValue(name) {
    return el.checkoutForm?.querySelector(`[data-photo-flow-field="${name}"]`)?.value.trim() || '';
  }

  function checkoutUrl(card) {
    const method = card.dataset.method;
    const params = new URLSearchParams();

    params.set('properties', base64Url(JSON.stringify({ _photoId: photoId })));

    const email = fieldValue('email');
    const phone = fieldValue('phone');
    if (email) params.set('checkout[email]', email);
    if (phone) params.set('attributes[Phone]', phone);

    if (method === 'mail') {
      const name = fieldValue('name');
      const split = name.lastIndexOf(' ');
      const first = split > 0 ? name.slice(0, split) : '';
      const last = split > 0 ? name.slice(split + 1) : name;
      const address = {
        first_name: first,
        last_name: last,
        address1: fieldValue('address1'),
        address2: fieldValue('address2'),
        city: fieldValue('city'),
        province: fieldValue('province'),
        zip: fieldValue('zip'),
        country: fieldValue('country'),
        phone,
      };

      Object.entries(address).forEach(([key, value]) => {
        if (value) params.set(`checkout[shipping_address][${key}]`, value);
      });
    }

    if (el.deliver?.dataset.locale) params.set('locale', el.deliver.dataset.locale);

    const base = (el.deliver?.dataset.cartUrl || '/cart').replace(/\/$/, '');

    return `${base}/${card.dataset.variantId}:1?${params.toString()}`;
  }

  el.checkoutForm?.addEventListener('submit', (event) => {
    event.preventDefault();

    const selected = selectedCard();
    if (!selected || !photoId || !el.deliver) return;

    if (!el.checkoutForm.checkValidity()) {
      el.checkoutForm.reportValidity();
      return;
    }

    if (el.cartError) el.cartError.dataset.visible = 'false';
    el.addToCart.disabled = true;
    if (el.ctaLabel) el.ctaLabel.textContent = el.addToCart.dataset.labelBusy || el.ctaLabel.textContent;

    try {
      rememberSession(false);
      window.location.href = checkoutUrl(selected);
    } catch {
      if (el.cartError) el.cartError.dataset.visible = 'true';
      el.addToCart.disabled = false;
      if (el.ctaLabel) el.ctaLabel.textContent = el.addToCart.dataset.label || el.ctaLabel.textContent;
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    if (step === 2) resumeCapture();
    if (!el.addToCart) return;
    el.addToCart.disabled = false;
    if (el.ctaLabel) el.ctaLabel.textContent = el.addToCart.dataset.label || el.ctaLabel.textContent;
  });

  window.addEventListener('pagehide', stopCamera);

  root.photoFlow = {
    advance,
    getPhoto: () => photo,
    getPhotoId: () => photoId,
  };
})();
